import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight, CheckCircle, Calendar, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollToTop from './ScrolltoTop';
import BlogPost from './blogPost';
// --- IMPORTS ---
import Activities from './Activities';
import UGTeam from './UGTeam';
import PGTeam from './PGTeam';
import ProjectsPage, { allProjects } from './Projects'; 
import Blogs from './Blogs'; 
import Research from './Research'; 
import Footer from './Footer';
import NotFound from './NotFound';

// --- CONFIGURATION ---
const CONFIG = {
  // Points to the Python script
  API_ENDPOINT: 'https://iitk.ac.in/eea/eea_python/rsvp.cgi', 
  ASSETS_PATH: process.env.PUBLIC_URL || ''
};

// --- SCROLL HELPER ---
const ScrollToAnchor = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.replace('#', ''));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hash]);
  return null;
};

// --- HERO SLIDER ---
const HeroSlider = () => {
  const [index, setIndex] = useState(0);
  const images = [
    `${process.env.PUBLIC_URL}/images/electro.webp`,
    `${process.env.PUBLIC_URL}/images/power.webp`,
    `${process.env.PUBLIC_URL}/images/signal.webp`
  ];
  useEffect(() => {
    const timer = setInterval(() => setIndex((prev) => (prev + 1) % images.length), 5000); 
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden bg-slate-900">
      <AnimatePresence initial={false}>
        <motion.div
          key={index}
          initial={{ x: '100%', zIndex: 20 }}
          animate={{ x: 0, zIndex: 20 }}
          exit={{ x: '-20%', zIndex: 0, transition: { duration: 1 } }}
          transition={{ x: { type: "tween", ease: "easeInOut", duration: 1 } }}
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{ backgroundImage: `url(${images[index]})` }}
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-slate-900/60 mix-blend-multiply z-30"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent z-30"></div>
    </div>
  );
};

// --- RSVP MODAL (Connected to Python CGI) ---
const RSVPModal = ({ isOpen, onClose, eventName }) => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      // 1. Send data to the Python CGI Script
      const response = await fetch(CONFIG.API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          ...formData, 
          event: eventName 
        })
      });
      
      const result = await response.json();
      
      // 2. Check result status
      if (result.status === 'success') {
        setStatus('success');
        setTimeout(() => {
          onClose();
          setStatus('idle');
          setFormData({ name: '', email: '', phone: '' });
        }, 3000);
      } else {
        setStatus('error');
        setErrorMessage(result.message || 'Server error. Please try again.');
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setStatus('error');
      setErrorMessage('Network error. Check your internet or try again later.');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div 
            initial={{ scale: 0.9, opacity: 0, y: 20 }} 
            animate={{ scale: 1, opacity: 1, y: 0 }} 
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden z-10"
          >
            {/* Modal Header */}
            <div className="bg-slate-900 p-6 text-white text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500 rounded-full blur-2xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
              <h3 className="text-2xl font-bold relative z-10">Reserve Your Spot</h3>
              <p className="text-slate-400 text-sm mt-1 relative z-10">{eventName}</p>
              <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors z-20">
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              {status === 'success' ? (
                <div className="text-center py-6">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
                    <CheckCircle size={32} />
                  </motion.div>
                  <h4 className="text-xl font-bold text-slate-800">Confirmed!</h4>
                  <p className="text-slate-500 mt-2 text-sm">We've sent a confirmation email to <br/> <span className="font-semibold text-slate-800">{formData.email}</span></p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {status === 'error' && (
                    <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100 flex items-start gap-2">
                      <div className="mt-0.5"><X size={14}/></div>
                      <span>{errorMessage}</span>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Full Name</label>
                    <input 
                      type="text" 
                      required
                      className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">IITK Email</label>
                    <input 
                      type="email" 
                      required
                      className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      placeholder="username@iitk.ac.in"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Phone Number</label>
                    <input 
                      type="tel" 
                      required
                      className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                      placeholder="98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>

                  <button 
                    disabled={status === 'loading'}
                    type="submit" 
                    className="w-full py-3.5 mt-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold rounded-lg shadow-lg hover:shadow-blue-500/30 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {status === 'loading' ? <Loader2 className="animate-spin" size={20} /> : 'Complete Registration'}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// --- SPOTLIGHT EVENT (Final: Internships Context) ---
const SpotlightEvent = ({ onOpenRSVP }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 12, hours: 4, mins: 30 });
  
  return (
    <section className="py-24 bg-[#0B1120] relative overflow-hidden flex items-center justify-center">
      
      {/* 1. CYBER GRID BACKGROUND */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      
      {/* 2. AMBIENT NEON GLOWS */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        
        {/* MAIN GLASS CARD */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative bg-slate-900/40 backdrop-blur-2xl border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl shadow-blue-900/20 flex flex-col lg:flex-row min-h-[600px]"
        >
          {/* Decorative Border Gradient */}
          <div className="absolute inset-0 border border-white/10 rounded-[2rem] pointer-events-none z-20"></div>

          {/* LEFT: CONTENT SIDE */}
          <div className="p-8 md:p-14 lg:w-[55%] flex flex-col justify-center relative z-20">
            
            {/* Live Badge */}
            <div className="flex items-center gap-4 mb-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-400/20 text-blue-400 text-xs font-bold uppercase tracking-widest shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
                Upcoming Event
              </div>
              <span className="text-slate-400 text-sm font-mono tracking-tight flex items-center gap-2">
                 <Calendar size={14} className="text-slate-500" /> Jan 05, 2026
              </span>
            </div>

            {/* Typography */}
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1] tracking-tight">
              Resume Building <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 animate-gradient-x">
                Workshop
              </span>
            </h2>

            <p className="text-lg text-slate-300 mb-10 max-w-lg font-light leading-relaxed border-l-2 border-blue-500/50 pl-6">
              The Internship Season is unforgiving. Your resume is important to get shortlisted. <br/>
              Join us to deconstruct the <strong>Perfect One-Page Resume</strong>—from structuring technical projects to beating ATS filters. Learn exactly what recruiters at top firms look for.
            </p>

            {/* SPEAKER CARDS (Internship Context) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 max-w-xl">
              {/* Speaker 1 */}
              <div className="group relative p-5 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-blue-500/30 transition-all duration-300 cursor-default">
                 <div className="flex flex-col">
                    <h4 className="text-white font-bold text-lg leading-tight group-hover:text-blue-400 transition-colors">Soham Panchal</h4>
                    <div className="h-0.5 w-8 bg-blue-500/50 my-2 group-hover:w-full transition-all duration-500"></div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Incoming Intern @ Google</span>
                 </div>
              </div>

              {/* Speaker 2 */}
              <div className="group relative p-5 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 hover:border-indigo-500/30 transition-all duration-300 cursor-default">
                 <div className="flex flex-col">
                    <h4 className="text-white font-bold text-lg leading-tight group-hover:text-indigo-400 transition-colors">Manan Jindal</h4>
                    <div className="h-0.5 w-8 bg-indigo-500/50 my-2 group-hover:w-full transition-all duration-500"></div>
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Incoming Intern @ Goldman Sachs</span>
                 </div>
              </div>
            </div>

            {/* CTA & Timer */}
            <div className="flex flex-col sm:flex-row items-center gap-8">
              <button 
                onClick={onOpenRSVP}
                className="group relative px-10 py-4 bg-white text-slate-950 font-black text-lg rounded-xl overflow-hidden shadow-[0_0_20px_rgba(255,255,255,0.3)] hover:shadow-[0_0_40px_rgba(59,130,246,0.6)] hover:scale-105 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-400 opacity-0 group-hover:opacity-20 transition-opacity"></div>
                <span className="relative z-10 flex items-center gap-2">
                  Reserve Seat <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </button>
            </div>
          </div>

          {/* RIGHT: IMAGE SHOWCASE */}
          <div className="lg:w-[45%] relative bg-gradient-to-br from-slate-900 to-black flex items-center justify-center p-8 overflow-hidden">
             
             {/* Decorative Circles behind image */}
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[radial-gradient(circle,rgba(59,130,246,0.15)_0%,transparent_70%)] blur-xl"></div>

             {/* The Image - Floating Animation */}
             <motion.img 
               initial={{ opacity: 0, scale: 0.8, y: 20 }}
               whileInView={{ opacity: 1, scale: 1, y: 0 }}
               animate={{ y: [0, -10, 0] }}
               transition={{ 
                 opacity: { duration: 0.8 },
                 y: { repeat: Infinity, duration: 6, ease: "easeInOut" }
               }}
               src={`${CONFIG.ASSETS_PATH}/images/resume_session.webp`} 
               alt="Masterclass Poster"
               className="relative z-10 w-full h-full object-contain max-h-[550px] drop-shadow-[0_20px_50px_rgba(0,0,0,0.5)]"
             />
          </div>

        </motion.div>
      </div>
    </section>
  );
};

// --- HOME PAGE COMPONENT ---
const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRSVPOpen, setIsRSVPOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  const featuredProjects = allProjects.slice(0, 4);
  const latestBlogs = [
    {
      title: "Arvind Krishna: Pioneering Leadership in Tech",
      date: "Oct 15, 2024",
      excerpt: "From IIT Kanpur to leading IBM. The journey of Arvind Krishna and his role in pioneering Hybrid Cloud and AI.",
      slug: "leadership-digital-age",
      category: "Alumni Spotlight"
    },
    {
      title: "Building Infosys: A Journey",
      date: "Sep 22, 2024",
      excerpt: "The legendary story of starting Infosys with just $250. Lessons on values and building a global giant.",
      slug: "building-infosys",
      category: "Entrepreneurship"
    }
  ];

  return (
    <>
      <header className="relative h-screen flex items-center overflow-hidden bg-slate-900">
        <HeroSlider /> 
        <div className="container mx-auto px-6 relative z-40 pt-20">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-cyan-300 text-sm font-medium backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span></span>
              Innovating at IIT Kanpur
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }} className="text-5xl lg:text-7xl font-extrabold text-white leading-tight mb-6">
              Electrifying the <br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">Future of Engineering</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }} className="text-lg text-slate-300 max-w-2xl leading-relaxed mb-8">
              The Electrical Engineering Association bridges the gap between theory and practice. Join a community of innovators building the next generation of tech.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }} className="flex flex-col sm:flex-row gap-4">
              <Link to="/projects" className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-lg shadow-blue-500/30 transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2">
                Explore Projects <ArrowRight size={20} />
              </Link>
            </motion.div>
          </div>
        </div>
      </header>

      {/* --- SPOTLIGHT EVENT SECTION --- */}
      <SpotlightEvent onOpenRSVP={() => setIsRSVPOpen(true)} />

      {/* About Section - UPDATED CONTENT */}
      <section id="about" className="py-24 bg-white relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            {/* TEXT SIDE */}
            <div className="lg:w-1/2">
              <h2 className="text-4xl font-extrabold text-slate-900 mb-8">About <span className="text-blue-600">EEA</span></h2>
              
              <div className="text-lg text-slate-600 leading-relaxed space-y-6 text-justify">
                <p>
                  The <span className="font-semibold text-slate-800">Electrical Engineering Association (EEA)</span> is the academic and research-driven student body of the Electrical Engineering department, dedicated to shaping how students learn, engage, and grow within the discipline.
                </p>
                <p>
                  Beyond organizing events, EEA functions as a structural bridge between students and faculty—strengthening academic preparedness, expanding access to undergraduate research, and fostering a culture of intellectual rigor and collaboration.
                </p>
                <p>
                  Through initiatives spanning orientation, academic support, faculty-led projects, research talks, large-scale programs, and digital outreach, EEA has transformed research from an opaque privilege into an accessible pathway and redefined student engagement within the department. Its mission is simple but ambitious: to build systems that empower students, sustain institutional memory, and elevate the undergraduate experience in Electrical Engineering.
                </p>
              </div>
            </div>

            {/* IMAGE SIDE */}
            <div className="lg:w-1/2 w-full relative">
                <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="relative rounded-3xl overflow-hidden shadow-2xl h-96 lg:h-[500px]">
                  <img src={`${process.env.PUBLIC_URL}/images/about.webp`} alt="IIT Kanpur Lab" className="w-full h-full object-cover" loading="lazy" />
                </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      <Activities />

      {/* Featured Projects with Skeleton */}
      {/* Featured Projects with Balanced Framing */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">Featured Projects</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <AnimatePresence mode="wait">
              {isLoading ? (
                [...Array(4)].map((_, i) => (
                  <div key={i} className="h-64 bg-slate-200 animate-pulse rounded-2xl" />
                ))
              ) : (
                featuredProjects.map((project) => (
                  <Link to="/projects" key={project.id}>
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.95 }} 
                      animate={{ opacity: 1, scale: 1 }} 
                      className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all group h-full flex flex-col border border-slate-100"
                    >
                      {/* --- UPDATED IMAGE CONTAINER --- */}
                      <div className="h-56 overflow-hidden relative bg-slate-100 flex items-center justify-center p-6">
                        <img 
                          src={project.image} 
                          alt={project.title} 
                          className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105" 
                          loading="lazy" 
                        />
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold text-slate-700 uppercase border border-slate-100 shadow-sm z-10">
                          {project.category}
                        </div>
                      </div>

                      <div className="p-8 flex-1 flex flex-col">
                        <h3 className="text-2xl font-bold text-slate-800 group-hover:text-blue-600 transition-colors mb-4 line-clamp-1">
                          {project.title}
                        </h3>
                        <p className="text-slate-600 mb-6 flex-1 line-clamp-2 leading-relaxed">
                          {project.desc}
                        </p>
                        <span className="text-blue-600 font-bold flex items-center gap-2 text-sm uppercase tracking-wider">
                          View Project <ArrowRight size={18} />
                        </span>
                      </div>
                    </motion.div>
                  </Link>
                ))
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Latest Blogs with Skeleton */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">Latest Blogs</h2>
            <Link to="/blogs" className="hidden sm:flex items-center gap-2 text-blue-600 font-bold hover:gap-4 transition-all">Go to Blogs <ArrowRight size={20} /></Link>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <AnimatePresence mode="wait">
              {isLoading ? [...Array(2)].map((_, i) => <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-2xl" />) : 
                latestBlogs.map((blog, idx) => (
                  <Link to={`/blogs/${blog.slug}`} key={idx}>
                    <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:bg-white hover:shadow-xl transition-all group">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full uppercase">{blog.category}</span>
                      <h3 className="text-2xl font-bold text-slate-800 my-4 group-hover:text-blue-600 transition-colors">{blog.title}</h3>
                      <p className="text-slate-600 mb-6 line-clamp-2">{blog.excerpt}</p>
                      <span className="text-slate-900 font-bold flex items-center gap-2 group-hover:gap-4 transition-all">Read Story <ArrowRight size={18} className="text-blue-600" /></span>
                    </div>
                  </Link>
                ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <section id="join" className="py-20 bg-blue-600 relative overflow-hidden">
        <div className="container mx-auto px-6 text-center relative z-10 text-white">
          <h2 className="text-4xl font-extrabold mb-6">Ready to Electrify Your Future?</h2>
          <p className="text-blue-100 text-xl max-w-2xl mx-auto mb-10">Join EEA IIT Kanpur to access exclusive workshops and opportunities.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`${process.env.PUBLIC_URL}/join_eea.pdf`} target="_blank" rel="noreferrer" className="px-8 py-4 bg-white text-blue-700 font-bold rounded-lg shadow-xl hover:bg-slate-100 transition-all transform hover:-translate-y-1">Join EEA Now</a>
            <a href="mailto:eea@iitk.ac.in" className="px-8 py-4 bg-blue-700 text-white font-bold rounded-lg border border-blue-400 hover:bg-blue-800 transition-all">Contact Us</a>
          </div>
        </div>
      </section>

      {/* --- RSVP MODAL RENDER --- */}
      <RSVPModal 
        isOpen={isRSVPOpen} 
        onClose={() => setIsRSVPOpen(false)} 
        eventName="Resume Building Workshop - Jan 05, 2026"
      />
    </>
  );
};

// --- NAVBAR COMPONENT ---
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation(); 
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navClass = isHome && !isScrolled ? 'bg-transparent py-5 text-white' : 'bg-white/90 backdrop-blur-md shadow-md py-3 text-slate-900';
  const linkClass = isHome && !isScrolled ? 'text-slate-200 hover:text-cyan-400' : 'text-slate-600 hover:text-blue-600';
  const dropdownClass = "absolute left-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden transform opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 origin-top-left invisible group-hover:visible";

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${navClass}`}>
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img src={`${process.env.PUBLIC_URL}/images/logo.webp`} alt="EEA Logo" className="h-12 w-auto drop-shadow-md" onError={(e) => {e.target.style.display = 'none'}} />
          <div className="flex flex-col leading-tight">
            <span className={`text-xl font-bold ${isHome && !isScrolled ? 'text-white' : 'text-slate-900'}`}>EEA</span>
            <span className={`text-xs ${isHome && !isScrolled ? 'text-slate-300' : 'text-slate-500'}`}>IIT Kanpur</span>
          </div>
        </Link>

        {/* Desktop links */}
        <div className="hidden xl:flex items-center space-x-6">
          <Link to="/" className={`text-sm font-medium ${linkClass}`}>Home</Link>
          <Link to="/#about" className={`text-sm font-medium ${linkClass}`}>About</Link>
          <Link to="/#activities" className={`text-sm font-medium ${linkClass}`}>Activities</Link>
          <Link to="/research" className={`text-sm font-medium ${linkClass}`}>Research</Link>
          <Link to="/projects" className={`text-sm font-medium ${linkClass}`}>Projects</Link>
          <Link to="/blogs" className={`text-sm font-medium ${linkClass}`}>Blogs</Link>
           <div className="relative group py-2">
            <button className={`text-sm font-medium flex items-center gap-1 ${linkClass}`}>Team <svg className="w-4 h-4 transition-transform group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg></button>
            <div className={dropdownClass}>
              <Link to="/team/ug" className="block px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors">UG Team</Link>
              <Link to="/team/pg" className="block px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors border-t border-slate-50">PG Team</Link>
            </div>
          </div>
          <a href={`${process.env.PUBLIC_URL}/join_eea.pdf`} target="_blank" rel="noreferrer" className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${isHome && !isScrolled ? 'bg-white text-blue-900' : 'bg-blue-600 text-white'}`}>Join EEA</a>
        </div>

        <button className={`xl:hidden ${isHome && !isScrolled ? 'text-white' : 'text-slate-900'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu Expanded - FIXED TEAM LINKS RESTORED */}
      {isMobileMenuOpen && (
        <div className="xl:hidden absolute top-full left-0 w-full bg-white shadow-xl border-t border-slate-100 py-6 flex flex-col px-6 space-y-4">
          <Link to="/" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          <Link to="/#about" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>About</Link>
          <Link to="/#activities" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Activities</Link>
          <Link to="/research" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Research</Link>
          {/* Restored Team Section for Mobile */}
          <div className="border-l-2 border-slate-100 pl-4 flex flex-col space-y-3">
             <span className="text-xs uppercase font-bold text-slate-400">Team</span>
             <Link to="/team/ug" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Undergraduate</Link>
             <Link to="/team/pg" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Postgraduate</Link>
          </div>
          <Link to="/projects" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Projects</Link>
          <Link to="/blogs" className="text-slate-600 font-medium" onClick={() => setIsMobileMenuOpen(false)}>Blogs</Link>
          <a href={`${process.env.PUBLIC_URL}/join_eea.pdf`} target="_blank" rel="noreferrer" className="text-blue-600 font-bold" onClick={() => setIsMobileMenuOpen(false)}>Join EEA</a>
        </div>
      )}
    </nav>
  );
};

// --- APP COMPONENT ---
const App = () => {
  return (
    <Router basename="/eea">
      <ScrollToAnchor />
      <ScrollToTop />
      <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
        <Navbar />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/team/ug" element={<UGTeam />} />
          <Route path="/team/pg" element={<PGTeam />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/blogs" element={<Blogs />} /> 
          <Route path="/blogs/:slug" element={<BlogPost />} />
          <Route path="/research" element={<Research />} />
          
          {/* CATCH-ALL 404 ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer /> 
      </div>
    </Router>
  );
};

export default App;